/**
 * CRM display labels (English only)
 */

/** Swimlane column order for CRM board view (excludes archived unless toggled). */
export const CRM_BOARD_STATUS_ORDER = [
  'new',
  'contacted',
  'intake_sent',
  'intake_received',
  'scheduling',
  'scheduled',
  'in_simple_practice',
  'on_hold',
  'lost',
  'not_interested',
]

export function getCrmBoardStatuses({ includeArchived = false, statusFilter = '' } = {}) {
  if (statusFilter) return [statusFilter]
  const statuses = [...CRM_BOARD_STATUS_ORDER]
  if (includeArchived) statuses.push('archived')
  return statuses
}

export const LEAD_STATUS_LABELS = {
  new: 'New',
  contacted: 'Contacted',
  intake_sent: 'Intake form sent',
  intake_received: 'Intake form received',
  scheduling: 'Scheduling',
  scheduled: 'Scheduled',
  in_simple_practice: 'In Simple Practice',
  on_hold: 'On hold',
  lost: 'Lost',
  not_interested: 'Not interested',
  archived: 'Archived',
}

export const PARTICIPANT_ROLE_LABELS = {
  contact: 'Primary contact',
  adult: 'Adult',
  guardian: 'Guardian',
  minor: 'Minor',
  partner_1: 'Partner 1',
  partner_2: 'Partner 2',
}

export const LEAD_SOURCE_LABELS = {
  whatsapp: 'WhatsApp',
  phone: 'Phone',
  psychology_today: 'Psychology Today',
  instagram: 'Instagram',
  referral: 'Referral',
  website_contact: 'Website — Contact',
  website_book: 'Website — Book session',
  website_intake: 'Website — Intake',
  unknown: 'Other',
}

/** Manual New case form — same order as the Source dropdown */
export const CRM_MANUAL_CREATE_SOURCES = [
  'whatsapp',
  'phone',
  'psychology_today',
  'instagram',
  'referral',
  'unknown',
]

/** Source order for CRM help (“Where cases come from”) */
export const CRM_HELP_SOURCE_ORDER = [
  ...CRM_MANUAL_CREATE_SOURCES,
  'website_contact',
  'website_book',
  'website_intake',
]

export const TIMELINE_EVENT_LABELS = {
  lead_created: 'Case created',
  note: 'Note',
  status_change: 'Status change',
  form_submitted: 'Form submitted',
  assignment: 'Assignment',
  call: 'Call',
  sp_migrated: 'Migrated to Simple Practice',
  idle_alert: 'Idle alert',
  client_followup: 'Client follow-up sent',
  client_message: 'Client message',
  marketing_consent: 'Marketing consent',
  assignee_nudge: 'Team reminder sent',
  dedup_conflict: 'Dedup conflict',
  message_sent: 'Message sent',
}

export function statusBadgeClass(status) {
  const map = {
    new: 'bg-blue-100 text-blue-800',
    contacted: 'bg-indigo-100 text-indigo-800',
    intake_sent: 'bg-purple-100 text-purple-800',
    intake_received: 'bg-violet-100 text-violet-800',
    scheduling: 'bg-amber-100 text-amber-800',
    scheduled: 'bg-yellow-100 text-yellow-900',
    in_simple_practice: 'bg-green-100 text-green-800',
    on_hold: 'bg-gray-100 text-gray-700',
    lost: 'bg-red-100 text-red-800',
    not_interested: 'bg-red-50 text-red-600',
    archived: 'bg-gray-200 text-gray-600',
  }
  return map[status] || 'bg-gray-100 text-gray-700'
}

export function formatTimelineSummary(event) {
  const description = formatTimelineDescription(event)
  if (description) return description
  return TIMELINE_EVENT_LABELS[event.event_type] || event.event_type
}

/** Short title for timeline row heading */
export function formatTimelineTitle(event) {
  return TIMELINE_EVENT_LABELS[event.event_type] || event.event_type
}

/** Optional body text below the title (null when title alone is enough) */
export function formatTimelineDescription(event) {
  const { event_type, payload } = event
  if (event_type === 'note' && payload?.text) {
    return payload.text
  }
  if (event_type === 'status_change' && payload?.from && payload?.to) {
    return `${LEAD_STATUS_LABELS[payload.from] || payload.from} → ${LEAD_STATUS_LABELS[payload.to] || payload.to}`
  }
  if (event_type === 'assignment') {
    const fromLabel =
      payload?.from_name ||
      (payload?.from == null ? 'Unassigned' : `User #${payload.from}`)
    const toLabel =
      payload?.to_name || (payload?.to == null ? 'Unassigned' : `User #${payload.to}`)
    return `Assigned from ${fromLabel} to ${toLabel}`
  }
  if (event_type === 'form_submitted') {
    if (payload?.manual) {
      return `Intake form entered manually (submission #${payload?.submission_id || ''})`
    }
    if (payload?.type === 'contact') {
      const lines = []
      if (payload.subject) lines.push(`Subject: ${payload.subject}`)
      if (payload.message) lines.push(payload.message)
      if (lines.length) return lines.join('\n\n')
      return `Contact form #${payload?.submission_id || ''}`
    }
    return `Form: ${payload?.type || 'submission'} #${payload?.submission_id || ''}`
  }
  if (event_type === 'sp_migrated') {
    return payload?.sp_reference
      ? `Reference: ${payload.sp_reference}`
      : null
  }
  if (event_type === 'client_followup') {
    const ch = (payload?.channels || []).join(', ') || payload?.channel || 'message'
    const n = payload?.attempt_number
    return n
      ? `Automated follow-up #${n} via ${ch}`
      : `Automated follow-up via ${ch}`
  }
  if (event_type === 'call') {
    if (payload?.direction === 'inbound') {
      const dur = payload.duration_seconds
      const status = payload.dial_status || payload.call_status || 'received'
      if (dur != null && dur > 0) {
        const mins = Math.floor(dur / 60)
        const secs = dur % 60
        const duration =
          mins > 0 ? `${mins} min${secs > 0 ? ` ${secs}s` : ''}` : `${secs}s`
        return `Inbound call — ${duration} — ${status}`
      }
      return `Inbound call — ${status}`
    }
    return null
  }
  if (event_type === 'client_message') {
    const preview = payload?.body_preview
    if (preview) {
      const text = String(preview).slice(0, 200)
      return `"${text}${preview.length > 200 ? '…' : ''}"`
    }
    const ch = payload?.channel || 'message'
    return `Inbound ${ch} message`
  }
  if (event_type === 'assignee_nudge') {
    const to = payload?.to_name || 'team member'
    const from = payload?.from_name
    const note = payload?.note
    let text = from ? `${from} sent a friendly reminder to ${to}` : `Friendly reminder sent to ${to}`
    if (note) text += ` — “${String(note).slice(0, 120)}${note.length > 120 ? '…' : ''}”`
    return text
  }
  if (event_type === 'marketing_consent') {
    if (payload?.consent === 1) return 'Marketing consent: yes'
    if (payload?.consent === 0) return 'Marketing consent: no'
    return 'Marketing consent cleared'
  }
  if (event_type === 'idle_alert') {
    return payload?.repeat
      ? `Idle reminder #${payload.repeat}`
      : 'Notification sent to the team'
  }
  if (event_type === 'dedup_conflict') {
    return payload?.message || 'Possible duplicate case detected'
  }
  if (event_type === 'message_sent' && payload?.kind === 'intake_link') {
    const lang = payload.language === 'pt' ? 'Portuguese' : 'English'
    const deliveries = payload.deliveries || []
    if (deliveries.length) {
      const parts = deliveries.map((d) => {
        const label = d.channel === 'sms' ? 'SMS' : 'Email'
        return `${label} to ${d.to || 'client'}`
      })
      const lines = [`Intake form link sent (${lang}): ${parts.join('; ')}`]
      if (payload.intake_url) lines.push(payload.intake_url)
      return lines.join('\n')
    }
    const lines = [`Intake form link emailed (${lang}) to ${payload.to || 'client'}`]
    if (payload.intake_url) lines.push(payload.intake_url)
    return lines.join('\n')
  }
  if (event_type === 'message_sent') {
    return payload?.channel
      ? `${payload.channel} message to ${payload.to || 'recipient'}`
      : null
  }
  return null
}

export function timelineAuthorInitial(name) {
  if (!name) return '?'
  const parts = String(name).trim().split(/\s+/).filter(Boolean)
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
  }
  return parts[0]?.slice(0, 2).toUpperCase() || '?'
}
